import React from 'react';
import { Button } from './ui/button';
import { 
  Home, 
  Package, 
  Settings, 
  FileText, 
  BarChart3, 
  LogOut, 
  Store,
  ChevronDown,
  CreditCard
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useNavigation } from '../contexts/NavigationContext';

function Navigation({ currentPage, shopName, onBack }) {
  const { navigate, logout } = useNavigation();
  const navItems = [
    { key: 'home', label: 'Home', icon: Home },
    { key: 'stockOnboarding', label: 'Stock Onboarding', icon: Package },
    { key: 'manageStock', label: 'Manage Stock', icon: Settings },
    { key: 'sheets', label: 'Sheets', icon: FileText },
    { key: 'reports', label: 'Reports', icon: BarChart3 },
    { key: 'subscription', label: 'Subscription', icon: CreditCard }
  ];

  // For sub-pages, show their parent page as active but don't include them in main nav
  const getActivePage = () => {
    if (currentPage === 'uploadInvoice' || currentPage === 'indentEstimate' || currentPage === 'shiftTransfer' || currentPage === 'addStore' || currentPage === 'stockReceived') {
      return 'manageStock'; // Show Manage Stock as active since these are accessed through it
    }
    if (currentPage === 'updateClosingStock' || currentPage === 'incomeExpenses' || currentPage === 'trackPayments') {
      return 'sheets'; // Show Sheets as active since these are accessed through it
    }
    if (currentPage === 'downloadSaleSheet' || currentPage === 'brandWiseSales' || currentPage === 'stockTransferReport' || currentPage === 'stockLifted' || currentPage === 'incomeExpensesReport') {
      return 'reports'; // Show Reports as active since these are accessed through it
    }
    return currentPage;
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center justify-between px-4 max-w-full">
        {/* Logo Section */}
        <div 
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('home')}
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Store className="h-6 w-6" />
          </div>
          <div className="text-left">
            <h1 className="text-lg font-bold leading-tight">{shopName}</h1>
            <p className="text-xs text-muted-foreground">Inventory Management</p>
          </div>
        </div>

        {/* Navigation - Desktop */}
        <nav className="hidden md:flex items-center gap-2">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = getActivePage() === item.key;
            return (
              <Button 
                key={item.key}
                variant={isActive ? "default" : "ghost"}
                size="sm"
                className="gap-2"
                onClick={() => {
                  // For sub-pages, use onBack when clicking their parent page
                  if (getActivePage() === item.key && currentPage !== item.key && onBack) {
                    onBack();
                  } else {
                    navigate(item.key);
                  }
                }}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Button>
            );
          })}
          
          <div className="ml-2 h-6 w-px bg-border" />
          
          <Button 
            variant="ghost" 
            size="sm"
            className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={logout}
          >
            <LogOut className="h-4 w-4" />
            Log Out
          </Button>
        </nav>

        {/* Mobile Navigation */}
        <div className="md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                Menu
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Navigation</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {navItems.map(item => {
                const Icon = item.icon;
                const isActive = getActivePage() === item.key;
                return (
                  <DropdownMenuItem 
                    key={item.key}
                    onClick={() => {
                      if (getActivePage() === item.key && currentPage !== item.key && onBack) {
                        onBack();
                      } else {
                        navigate(item.key);
                      }
                    }}
                    className={isActive ? "bg-primary text-primary-foreground" : ""}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {item.label}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={logout}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

export default Navigation;
